import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, RotateCcw } from "lucide-react";

/**
 * QRScanner: on-mount tries to start the back-camera. Emits onScan(text) when a code decodes.
 * Handles permission-denied / no-camera / unsupported states with actionable messages.
 * Includes a manual token paste fallback below the viewport.
 */
export default function QRScanner({ onScan, active, onManualSubmit }) {
    const containerId = "pm-qr-scanner-viewport";
    const scannerRef = useRef(null);
    const [state, setState] = useState("initializing"); // initializing | running | denied | unavailable | error | stopped
    const [errorMsg, setErrorMsg] = useState("");
    const [manualToken, setManualToken] = useState("");

    useEffect(() => {
        if (!active) return;
        let mounted = true;

        const start = async () => {
            try {
                setState("initializing");
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    if (mounted) {
                        setState("unavailable");
                        setErrorMsg("Camera is not supported in this browser.");
                    }
                    return;
                }
                const scanner = new Html5Qrcode(containerId, { verbose: false });
                scannerRef.current = scanner;

                const config = { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 };
                try {
                    await scanner.start({ facingMode: "environment" }, config, (decoded) => {
                        onScan?.(decoded);
                    }, () => {}); // frame errors are noisy — swallow
                    if (mounted) setState("running");
                } catch (e) {
                    // Try any camera as a fallback
                    try {
                        const cameras = await Html5Qrcode.getCameras();
                        if (!cameras || cameras.length === 0) {
                            if (mounted) {
                                setState("unavailable");
                                setErrorMsg("No camera detected on this device.");
                            }
                            return;
                        }
                        await scanner.start(cameras[0].id, config, (decoded) => {
                            onScan?.(decoded);
                        }, () => {});
                        if (mounted) setState("running");
                    } catch (e2) {
                        const msg = String(e2 || e || "").toLowerCase();
                        if (mounted) {
                            if (msg.includes("permission") || msg.includes("notallowed")) {
                                setState("denied");
                                setErrorMsg("Camera permission was denied. Enable it in your browser settings and try again.");
                            } else {
                                setState("error");
                                setErrorMsg("Could not start camera. Use manual entry below.");
                            }
                        }
                    }
                }
            } catch (e) {
                if (mounted) {
                    setState("error");
                    setErrorMsg("Unexpected camera error.");
                }
            }
        };

        start();

        return () => {
            mounted = false;
            const s = scannerRef.current;
            if (s) {
                try {
                    const p = s.stop();
                    if (p && typeof p.then === "function") {
                        p.then(() => { try { s.clear(); } catch (_) {} }).catch(() => {});
                    } else {
                        try { s.clear(); } catch (_) {}
                    }
                } catch (_) {
                    // scanner wasn't running yet — nothing to stop
                    try { s.clear(); } catch (__) {}
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    const handleManualSubmit = () => {
        const t = manualToken.trim();
        if (!t) return;
        onManualSubmit ? onManualSubmit(t) : onScan?.(t);
        setManualToken("");
    };

    return (
        <div className="w-full" data-testid="qr-scanner">
            <div className="relative w-full max-w-[420px] mx-auto aspect-square bg-[#0a0a0a] border border-[#2a2a2a]">
                <div id={containerId} className="absolute inset-0 [&_video]:w-full [&_video]:h-full [&_video]:object-cover" />

                {state === "initializing" && (
                    <div className="absolute inset-0 flex items-center justify-center text-[10px] font-mono uppercase text-[#a3a3a3]" data-testid="qr-scanner-initializing">
                        Starting camera…
                    </div>
                )}
                {state === "running" && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div className="w-[60%] aspect-square border-2 border-[#ff4400]" />
                    </div>
                )}
                {(state === "denied" || state === "unavailable" || state === "error") && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center bg-[#141414]" data-testid={`qr-scanner-${state}`}>
                        <CameraOff className="w-8 h-8 text-[#ff3333]" />
                        <div className="text-sm text-[#f4f4f0]">{errorMsg}</div>
                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">
                            You can still paste the QR value below or use manual attendee search.
                        </div>
                    </div>
                )}
            </div>

            {/* Manual paste fallback */}
            <div className="mt-4 max-w-[420px] mx-auto">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-1">Manual entry (fallback)</div>
                <div className="flex gap-2">
                    <input
                        value={manualToken}
                        onChange={(e) => setManualToken(e.target.value)}
                        placeholder="Paste QR value here"
                        onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
                        className="flex-1 bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none text-sm"
                        data-testid="qr-manual-input"
                    />
                    <button onClick={handleManualSubmit} className="pm-btn pm-btn-primary text-xs" data-testid="qr-manual-submit">
                        Scan
                    </button>
                </div>
                <div className="mt-1 text-[10px] font-mono uppercase text-[#666] flex items-center gap-1">
                    <Camera className="w-3 h-3" /> Camera state: <span className="text-[#f4f4f0]">{state}</span>
                </div>
            </div>
        </div>
    );
}
